import type { FC, ChangeEvent } from 'react'
import { useState, useMemo, useEffect } from 'react'

import { Input, Select, Popup, InfoBox, LoaderCircle, AssetFallbackIcon } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { DECIMAL_PATTERN, getPricePrecision, MAX_DECIMAL_12_4, MIN_PRICE } from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon'

import { queryClient } from '@/app/queryClient'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import type { AssetExtended } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { createBuyoutOffer } from '@/modules/admin/resources/buyout-offers'

import { getEligibleEmployees, isAssetAssignedToEmployee } from '../helpers/saleEligibility'

type SellAssetModalProps = {
  assetDetails: AssetExtended
  isOpen: boolean
  onClose: () => void
}

export const SellAssetModal: FC<SellAssetModalProps> = ({ assetDetails, isOpen, onClose }) => {
  const translate = useTranslation()
  const profile = useProfile()
  if (!profile || !assetDetails.customerId) throw new Error('No profile found for SellAssetModal!')

  const { data: employees = [], isLoading: isEmployeesLoading } = useQuery(
    queries.adminEmployees(assetDetails.customerId),
    () => adminResources.getEmployees(false),
    { enabled: isOpen && Boolean(assetDetails.customerId) }
  )

  const eligibleEmployees = useMemo(
    () => getEligibleEmployees(assetDetails, employees),
    [assetDetails, employees]
  )

  useEffect(() => {
    if (eligibleEmployees.length === 1) {
      setSelectedEmployeeId(eligibleEmployees[0].id)
    } else {
      setSelectedEmployeeId(undefined)
    }
  }, [eligibleEmployees, assetDetails])

  // Default values
  const defaultPrice = assetDetails.priceBuyout || 0
  const defaultExpirationDate = new Date()
  defaultExpirationDate.setDate(defaultExpirationDate.getDate() + 7) // 1 week from now

  const [price, setPrice] = useState(defaultPrice.toString())
  const [expirationDate, setExpirationDate] = useState(
    defaultExpirationDate.toISOString().split('T')[0]
  )
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined)

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim()
    if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
    setPrice(newValue)
  }

  const createOfferMutation = useMutation(createBuyoutOffer, {
    onSuccess: () => {
      queryClient.refetchQueries(queries.adminAsset(assetDetails.id))
      queryClient.refetchQueries(queries.adminActiveBuyoutOfferByAsset(assetDetails.id))

      onClose()
      resetForm()
    },
  })

  const handleSubmit = () => {
    if (!selectedEmployeeId) {
      alert(translate('Please select an employee'))
      return
    }

    if (!price || parseFloat(price) <= 0) {
      alert(translate('Please enter a valid price'))
      return
    }

    if (!expirationDate) {
      alert(translate('Please select an expiration date'))
      return
    }

    createOfferMutation.mutate({
      assetId: assetDetails.id,
      employeeId: selectedEmployeeId,
      price: parseFloat(price),
      expirationDate,
    })
  }

  const resetForm = () => {
    setPrice(defaultPrice.toString())
    setExpirationDate(defaultExpirationDate.toISOString().split('T')[0])
    setSelectedEmployeeId(eligibleEmployees.length === 1 ? eligibleEmployees[0].id : undefined)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const isAssignedToEmployee = isAssetAssignedToEmployee(assetDetails)
  const employeeOptions = eligibleEmployees.map(emp => ({
    value: emp.id,
    label: `${emp.name}${emp.code ? ` - ${emp.code}` : ''}`,
  }))

  const formattedPriceValue = (value: number) => {
    const precision = getPricePrecision(value)
    return isNaN(value) ? '' : value.toFixed(precision)
  }

  // Loading state
  if (isEmployeesLoading) {
    return (
      <Popup
        isOpen={isOpen}
        variant="narrow"
        ariaLabel={translate('Sell Asset')}
        onClose={handleClose}
        className="min-h-56"
      >
        <div className="flex min-h-56 items-center justify-center">
          <LoaderCircle />
        </div>
      </Popup>
    )
  }

  return (
    <Popup
      isOpen={isOpen}
      variant="narrow"
      ariaLabel={translate('Sell Asset')}
      onClose={handleClose}
    >
      <h2 className="mb-2">
        <Translation id="Sell Asset" />
      </h2>
      <div className="mb-4 flex gap-3">
        <div className="flex items-center justify-center">
          <ImageWithIconFallback
            src={assetDetails.imageUrl || ''}
            className="h-12 w-12"
            alt="asset"
            fallbackIcon={<AssetFallbackIcon assetType={assetDetails.type} />}
          />
        </div>
        <div className="flex flex-col justify-center">
          <h3 className="mb-0 text-sm">{assetDetails.assetDescription}</h3>
          <div className="flex gap-3">
            {assetDetails.imei && (
              <span className="text-sm text-gray-600">
                <Translation id="IMEI" />: {assetDetails.imei}
              </span>
            )}
            {assetDetails.snr && (
              <span className="text-sm text-gray-600">
                <Translation id="Serialnbr" />: {assetDetails.snr}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Input
          capitalizeLabel
          isFullWidth
          id="price"
          type="number"
          inputMode="decimal"
          min={String(MIN_PRICE)}
          max={String(MAX_DECIMAL_12_4)}
          pattern="\d*"
          step="0.01"
          LeftElement={<span>{assetDetails.assetCurrency || profile?.currency}</span>}
          ariaLabel={translate('Price')}
          label={
            <>
              <Translation id="Price" />*
            </>
          }
          marginBottom={8}
          value={price}
          onChange={handlePriceChange}
          onBlur={() => {
            const numPrice = parseFloat(price)
            if (!isNaN(numPrice)) {
              setPrice(formattedPriceValue(numPrice))
            }
          }}
        />
      </div>
      <Input
        capitalizeLabel
        isFullWidth
        id="expirationDate"
        type="date"
        ariaLabel={translate('Offer valid to')}
        label={
          <>
            <Translation id="Offer valid to" />*
          </>
        }
        marginBottom={16}
        value={expirationDate}
        onChange={e => setExpirationDate(e.target.value)}
        min={new Date().toISOString().split('T')[0]}
      />

      <Select
        capitalizeLabel
        id="employeeId"
        label={
          <>
            <Translation id="Employee" />*
            {isAssignedToEmployee && profile.featureAccess.internal_sales && (
              <InfoBox emphasis="info" className="mt-2">
                <div className="flex items-center gap-3">
                  <AlertCircleOutlineIcon className="" />
                  <p className="m-0">
                    <Translation id="This asset is currently in use and can only be sold to the employee using it." />
                  </p>
                </div>
              </InfoBox>
            )}
          </>
        }
        options={employeeOptions}
        value={selectedEmployeeId}
        onChange={option => setSelectedEmployeeId(option?.value || undefined)}
        marginBottom={16}
        isDisabled={isAssignedToEmployee}
      />
      <footer className="flex justify-end gap-2">
        <Button variant="tertiary" onClick={handleClose}>
          <Translation id="Cancel" />
        </Button>
        <Button variant="primary" isLoading={createOfferMutation.isLoading} onClick={handleSubmit}>
          <Translation id="Create Offer" />
        </Button>
      </footer>
    </Popup>
  )
}
